import React from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MAX_BODY_LENGTH, useDivisionOptions, useTeamOptions } from '../../../state/master';
import { getFiscalYear, getFiscalYearRange, toFiscalYearLabel } from '../../../lib/common';
import { Snackbar } from '../../common/Snackbar/Snackbar';

import SettingDialog from './SettingDialog';

type TeamItem = {
  id: string;
  name: string;
  sourceIndex?: number;
  isNew?: boolean;
  refHint?: string;
  hasRef?: boolean;
  refMeta?: { divisionId: number; teamId: number } | null;
  copySameEnlightConnection?: boolean;
};
type TeamBuckets = {
  latest: string[];
  disabled: string[];
  pastByFy: Record<number, string[]>;
};
type TeamUiState = {
  value: string;
  bodyMax: string;
  refHint: string;
  startFiscalYear: number | null;
};

const CONTAINER_LATEST = 'latest';
const CONTAINER_DISABLED = 'disabled';
const pastContainerId = (year: number) => `past-${year}`;

const parsePastContainerYear = (containerId: string): number | null => {
  if (!containerId.startsWith('past-')) return null;
  const y = Number(containerId.replace('past-', ''));
  return Number.isFinite(y) ? y : null;
};

const DroppableArea: React.FC<{ id: string; active: boolean; children: React.ReactNode }> = ({ id, active, children }) => {
  const { setNodeRef } = useDroppable({ id });
  return (
    <Box
      ref={setNodeRef}
      sx={{
        borderRadius: 1,
        outline: active ? '2px solid #2e7d32' : '2px solid transparent',
        outlineOffset: 1,
      }}
    >
      {children}
    </Box>
  );
};

const TeamCard: React.FC<{
  item: TeamItem;
  containerId: string;
  isNew?: boolean;
  refTooltip?: React.ReactNode;
  ui: TeamUiState;
  variant?: 'current' | 'past' | 'archived';
  fiscalYears?: number[];
  onStartFiscalYearChange?: (id: string, year: number | null) => void;
  onValueChange: (id: string, value: string) => void;
  onBodyMaxChange: (id: string, value: string) => void;
}> = ({
  item,
  containerId,
  isNew,
  refTooltip,
  ui,
  variant = 'current',
  fiscalYears = [],
  onStartFiscalYearChange,
  onValueChange,
  onBodyMaxChange,
}) => {
  const { palette } = useTheme();
  const isDark = palette.mode === 'dark';
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    data: { containerId },
  });

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{
        border: isDark ? '1px solid #334155' : '1px solid #cfcfcf',
        borderRadius: 1,
        backgroundColor: variant === 'current' ? (isDark ? '#243044' : '#eef6ff') : (isDark ? '#1e293b' : '#c4c4c4'),
        p: variant === 'current' ? 1 : 0.75,
        boxShadow: isDragging ? '0 4px 10px rgba(0,0,0,0.14)' : 'none',
      }}
    >
      <Grid container spacing={1}>
        <Grid size={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip
              title={item.name}
              placement="top"
              arrow
              disableInteractive
              slotProps={{
                tooltip: {
                  sx: {
                    backgroundColor: '#424242',
                    color: '#fff',
                    fontSize: '0.875rem',
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 1,
                  },
                },
              }}
            >
              <Box
                sx={{ display: 'inline-flex', alignItems: 'center', color: isDark ? '#94a3b8' : '#666', cursor: isDragging ? 'grabbing' : 'grab' }}
                {...attributes}
                {...listeners}
              >
                <DragIndicatorIcon fontSize="small" />
              </Box>
            </Tooltip>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  lineHeight: 1.1,
                  mb: 0.25,
                  fontSize: variant === 'current' ? undefined : '0.56rem',
                }}
              >
                チーム名
              </Typography>
              <TextField
                size={variant === 'current' ? 'small' : 'small'}
                fullWidth
                label={undefined}
                value={ui.value}
                onChange={(e) => onValueChange(item.id, e.target.value)}
                sx={{
                  '.MuiInputBase-input': {
                    fontSize: variant === 'current' ? '0.95rem' : '0.64rem',
                    py: variant === 'current' ? undefined : 0.35,
                  },
                  ...(variant !== 'current'
                    ? {
                        '.MuiInputBase-root': {
                          minHeight: 22,
                          height: 22,
                        },
                      }
                    : {}),
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ ml: 0.5 }}>
                      {isNew ? (
                        <Chip
                          icon={<NewReleasesIcon sx={{ fontSize: 14 }} />}
                          label="New"
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{
                            height: 18,
                            mr: 0.5,
                            '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' },
                            '& .MuiChip-icon': { ml: 0.4, mr: -0.2 },
                          }}
                        />
                      ) : null}
                      {item.hasRef ? (
                        <Tooltip title={refTooltip ?? '参照元が設定されています'} placement="top">
                          <ErrorOutlineIcon sx={{ color: 'warning.main', cursor: 'help' }} fontSize="small" />
                        </Tooltip>
                      ) : null}
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            {variant === 'current' && (
              <Box sx={{ width: 92 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.1, mb: 0.25, whiteSpace: 'nowrap' }}>
                  本文最大文字
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  value={ui.bodyMax}
                  onChange={(e) => onBodyMaxChange(item.id, e.target.value)}
                  inputProps={{ min: 0, step: 1 }}
                  placeholder={String(MAX_BODY_LENGTH)}
                  sx={{ width: 92 }}
                />
              </Box>
            )}
            {variant === 'past' && (
              <Box sx={{ minWidth: 108 }}>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.1, mb: 0.25, fontSize: '0.56rem' }}
                >
                  チーム設立年度
                </Typography>
                <Select
                  size="small"
                  displayEmpty
                  value={ui.startFiscalYear == null ? '' : String(ui.startFiscalYear)}
                  onChange={(e) => {
                    const v = String(e.target.value ?? '');
                    onStartFiscalYearChange?.(item.id, v === '' ? null : Number(v));
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  sx={{
                    minWidth: 108,
                    backgroundColor: isDark ? '#1e293b' : '#fff',
                    '.MuiSelect-select': {
                      fontSize: '0.64rem',
                      py: 0.35,
                    },
                    minHeight: 22,
                    height: 22,
                  }}
                >
                  <MenuItem value="">未入力</MenuItem>
                  {fiscalYears.filter((year) => year !== 2024).map((year) => (
                    <MenuItem key={year} value={String(year)}>
                      {`FY${String(year).slice(-2)}`}
                    </MenuItem>
                  ))}
                  <MenuItem key="fy25-or-earlier" value="2024">
                    FY24以前
                  </MenuItem>
                </Select>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

function TeamSettingDialog(props: any) {
  const { open, rows, meta, onAccept } = props;
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';

  const [portalTarget, setPortalTarget] = React.useState<HTMLElement | null>(null);
  const hiddenCardRef = React.useRef<HTMLElement | null>(null);
  const mountRef = React.useRef<HTMLElement | null>(null);

  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [overContainerId, setOverContainerId] = React.useState<string | null>(null);
  const [notifyOpen, setNotifyOpen] = React.useState(false);
  const [notifyMsg, setNotifyMsg] = React.useState('');
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));
  const [derivedRows, setDerivedRows] = React.useState<TeamItem[]>([]);
  const observerRef = React.useRef<MutationObserver | null>(null);
  const derivedRowsSigRef = React.useRef<string>('');
  const { divisionOptions } = useDivisionOptions();
  const { teamOptions } = useTeamOptions();

  const sourceRows = React.useMemo(() => {
    if (Array.isArray((meta as any)?.options) && (meta as any).options.length > 0) return (meta as any).options;
    return Array.isArray(rows) ? rows : [];
  }, [meta, rows]);

  const toYearNumber = React.useCallback((value: any): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '') return null;
      const parsed = Number(trimmed);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }, []);

  const fiscalYears = React.useMemo(() => {
    const fixedStartYear = getFiscalYearRange('FY25').fiscalYear;
    const currentFiscalYear = getFiscalYear(new Date());
    let minDataYear: number | null = null;
    sourceRows.forEach((row: any) => {
      const startY = toYearNumber((row as any)?.start_fiscal_year ?? (row as any)?.team_start_fiscal_year);
      const endY = toYearNumber((row as any)?.end_fiscal_year ?? (row as any)?.team_fiscal_year);
      if (startY != null) minDataYear = minDataYear == null ? startY : Math.min(minDataYear, startY);
      if (endY != null) minDataYear = minDataYear == null ? endY : Math.min(minDataYear, endY);
    });

    const startYear = minDataYear == null ? fixedStartYear : Math.min(fixedStartYear, minDataYear);
    const endYear = Math.max(startYear, currentFiscalYear);
    const years: number[] = [];
    for (let y = endYear; y >= startYear; y--) years.push(y);
    return years;
  }, [sourceRows, toYearNumber]);

  const toFyLabel = React.useCallback((year: number) => toFiscalYearLabel(year), []);

  const readTeamItemsFromHiddenCard = React.useCallback((card: HTMLElement | null): TeamItem[] => {
    if (!card) return [];
    const result: TeamItem[] = [];
    const isDisabledRow = (row: any) => String(row?.status ?? 'Y').toUpperCase() === 'N';
    const enabledSourceIndexes = sourceRows
      .map((row: any, index: number) => ({ row, index }))
      .filter((entry: { row: any; index: number }) => !isDisabledRow(entry.row))
      .map((entry: { row: any; index: number }) => entry.index);
    const disabledSourceIndexes = sourceRows
      .map((row: any, index: number) => ({ row, index }))
      .filter((entry: { row: any; index: number }) => isDisabledRow(entry.row))
      .map((entry: { row: any; index: number }) => entry.index);

    const appendRows = (rowsInTable: HTMLTableRowElement[], sourceIndexes: number[], section: 'enabled' | 'disabled') => {
      rowsInTable.forEach((tr, localIndex) => {
        const sourceIndex = sourceIndexes[localIndex];
        const input = tr.querySelector('input[id="column-name"]') as HTMLInputElement | null;
        const sourceRow = Number.isFinite(sourceIndex) ? (sourceRows[sourceIndex] ?? {}) : {};
        const name = String(input?.value ?? (sourceRow as any)?.value ?? (sourceRow as any)?.label ?? '').trim();
        if (!name) return;

        // 新規追加行は SettingDialog 側で付与された安定識別子(data-ui-uid)を優先して使う。
        // sourceRows はダイアログを開いた時点のスナップショットで、セッション中に追加された行を含まないため、
        // 位置(sourceIndex)ベースの照合だけでは複数行を同時追加した際にズレが生じる。
        const domUid = String(tr.getAttribute('data-ui-uid') ?? '').trim();

        const endAdornment = tr.querySelector('.MuiInputAdornment-root');
        const isNewBySource = Boolean((sourceRow as any)?.__ui_new);
        const isNewByDom = Array.from(endAdornment?.querySelectorAll('.MuiChip-label') ?? []).some(
          (labelEl) => String(labelEl.textContent ?? '').trim().toLowerCase() === 'new'
        );
        const isNew = isNewBySource || isNewByDom;

        let refHint = '参照なし';
        let refMeta: { divisionId: number; teamId: number } | null = null;
        // sourceRows はダイアログ表示時点のスナップショットのため、セッション中に追加した行の参照設定は
        // 含まれない。その場合は SettingDialog が行に出力した data-ui-ref-* 属性から復元する。
        const domRefOrg = tr.getAttribute('data-ui-ref-org') === 'use'
          ? {
              orgSelection: 'use',
              divisionId: Number(tr.getAttribute('data-ui-ref-division') ?? 0),
              teamId: Number(tr.getAttribute('data-ui-ref-team') ?? 0),
              copySameEnlightConnection: tr.getAttribute('data-ui-ref-copy-enlight') === '1',
            }
          : null;
        const refOrg = (sourceRow as any)?.__ui_ref_org ?? domRefOrg;
        const usingFlg = Boolean((sourceRow as any)?.other_setting_using);
        if (refOrg?.orgSelection === 'use') {
          refMeta = {
            divisionId: Number(refOrg?.divisionId ?? 0),
            teamId: Number(refOrg?.teamId ?? 0),
          };
        } else if (usingFlg) {
          refMeta = {
            divisionId: Number((sourceRow as any)?.using_department ?? 0),
            teamId: Number((sourceRow as any)?.using_team ?? 0),
          };
        }
        if (refMeta) {
          refHint = `参照元: 課${refMeta.divisionId} / チーム${refMeta.teamId}`;
        }
        const copySameEnlightConnection = Boolean(
          refOrg?.copySameEnlightConnection ?? (sourceRow as any)?.copy_same_enlight_connection
        );
        const hasRefByDom = Boolean(endAdornment?.querySelector(':scope > svg'));
        const hasRef = Boolean(refMeta) || hasRefByDom;

        result.push({
          id: domUid ? `uid-${domUid}` : (Number.isFinite(sourceIndex) ? `src-${sourceIndex}` : `dom-${section}-${localIndex}`),
          name,
          sourceIndex: Number.isFinite(sourceIndex) ? sourceIndex : undefined,
          isNew,
          hasRef,
          refMeta,
          refHint,
          copySameEnlightConnection,
        });
      });
    };

    const enabledTable = card.querySelector('table[aria-label="enabled table"]');
    const disabledTable = card.querySelector('table[aria-label="disabled table"]');
    const enabledRows = Array.from(enabledTable?.querySelectorAll('tbody tr') ?? []) as HTMLTableRowElement[];
    const disabledRows = Array.from(disabledTable?.querySelectorAll('tbody tr') ?? []) as HTMLTableRowElement[];

    appendRows(enabledRows, enabledSourceIndexes, 'enabled');
    appendRows(disabledRows, disabledSourceIndexes, 'disabled');
    return result;
  }, [sourceRows]);

  const divisionLabelById = React.useMemo(() => {
    const map = new Map<number, string>();
    (divisionOptions ?? []).forEach((opt: any) => {
      const id = Number((opt as any)?.department_id);
      if (!Number.isFinite(id)) return;
      map.set(id, String((opt as any)?.label ?? ''));
    });
    return map;
  }, [divisionOptions]);

  const teamLabelById = React.useMemo(() => {
    const map = new Map<number, string>();
    (teamOptions ?? []).forEach((opt: any) => {
      const id = Number((opt as any)?.team_id);
      if (!Number.isFinite(id)) return;
      map.set(id, String((opt as any)?.label ?? ''));
    });
    return map;
  }, [teamOptions]);

  const syncDerivedRowsFromCard = React.useCallback((card: HTMLElement | null) => {
    const nextRows = readTeamItemsFromHiddenCard(card);
    const sig = nextRows
      .map(
        (r) =>
          `${r.id}:${r.name}:${r.sourceIndex ?? ''}:${r.isNew ? 1 : 0}:${r.hasRef ? 1 : 0}:${r.refHint ?? ''}:${r.copySameEnlightConnection ? 1 : 0}`
      )
      .join('|');
    if (sig === derivedRowsSigRef.current) return;
    derivedRowsSigRef.current = sig;
    setDerivedRows(nextRows);
  }, [readTeamItemsFromHiddenCard]);

  const teamRows = React.useMemo(() => {
    if (derivedRows.length > 0) return derivedRows;
    const mapped = sourceRows.map((row: any, index: number): TeamItem => {
      const id = String(row?.team_id ?? row?.option_id ?? row?.value ?? index);
      const name = String(row?.label ?? row?.value ?? `チーム${index + 1}`);
      return { id, name };
    });
    return mapped.length > 0 ? mapped : [{ id: 'current-team', name: '選択中チーム（サンプル）' }];
  }, [derivedRows, sourceRows]);

  const [uiById, setUiById] = React.useState<Record<string, TeamUiState>>({});

  const buildEmptyPastByFy = React.useCallback((): Record<number, string[]> => {
    const obj: Record<number, string[]> = {};
    fiscalYears.forEach((year) => {
      obj[year] = [];
    });
    return obj;
  }, [fiscalYears]);

  const [buckets, setBuckets] = React.useState<TeamBuckets>({ latest: [], disabled: [], pastByFy: {} });
  const bucketsRef = React.useRef<TeamBuckets>(buckets);

  React.useEffect(() => {
    bucketsRef.current = buckets;
  }, [buckets]);

  React.useEffect(() => {
    if (!open) {
      setPortalTarget(null);
      return;
    }

    let cancelled = false;
    const attach = () => {
      if (cancelled) return false;
      const root = document.querySelector('.setting-dialog-root') as HTMLElement | null;
      if (!root) return false;

      const cards = Array.from(root.querySelectorAll('.MuiCard-root')) as HTMLElement[];
      const targetCard = cards.find((card) => {
        const cap = card.querySelector('.MuiTypography-caption');
        const txt = String(cap?.textContent ?? '').trim();
        return txt.includes('値・色設定、および有効/無効化');
      });

      if (targetCard) {
        hiddenCardRef.current = targetCard;
        hiddenCardRef.current.style.display = 'none';
        syncDerivedRowsFromCard(targetCard);

        if (observerRef.current) {
          observerRef.current.disconnect();
        }
        observerRef.current = new MutationObserver(() => {
          syncDerivedRowsFromCard(targetCard);
        });
        observerRef.current.observe(targetCard, {
          childList: true,
          subtree: true,
        });

        const mount = document.createElement('div');
        mount.setAttribute('data-team-setting-mock-anchor', '1');
        targetCard.parentElement?.insertBefore(mount, targetCard);
        mountRef.current = mount;
        setPortalTarget(mount);
      } else {
        // target card が見つからない場合でも root に描画して視認できるようにする
        setPortalTarget(root);
      }
      return true;
    };

    if (!attach()) {
      const timer = window.setInterval(() => {
        if (attach()) {
          window.clearInterval(timer);
        }
      }, 50);

      return () => {
        cancelled = true;
        window.clearInterval(timer);
        if (hiddenCardRef.current) {
          hiddenCardRef.current.style.display = '';
          hiddenCardRef.current = null;
        }
        if (observerRef.current) {
          observerRef.current.disconnect();
          observerRef.current = null;
        }
        if (mountRef.current && mountRef.current.parentElement) {
          mountRef.current.parentElement.removeChild(mountRef.current);
        }
        mountRef.current = null;
        derivedRowsSigRef.current = '';
        setDerivedRows([]);
      };
    }

    return () => {
      cancelled = true;
      if (hiddenCardRef.current) {
        hiddenCardRef.current.style.display = '';
        hiddenCardRef.current = null;
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (mountRef.current && mountRef.current.parentElement) {
        mountRef.current.parentElement.removeChild(mountRef.current);
      }
      mountRef.current = null;
      derivedRowsSigRef.current = '';
      setDerivedRows([]);
    };
  }, [open, syncDerivedRowsFromCard]);

  React.useEffect(() => {
    if (!open || fiscalYears.length === 0) return;

    const bySourceId = new Map<string, any>();
    sourceRows.forEach((row: any, index: number) => {
      const id = String(row?.team_id ?? row?.option_id ?? row?.value ?? index);
      bySourceId.set(id, row);
    });

    setUiById((prev) => {
      const next: Record<string, TeamUiState> = {};
      teamRows.forEach((t: TeamItem) => {
        const row = bySourceId.get(t.id) ?? (typeof t.sourceIndex === 'number' ? (sourceRows[t.sourceIndex] ?? {}) : {});
        const refOrg = (row as any)?.__ui_ref_org;
        const refHint = t.refHint ?? (
          refOrg?.orgSelection === 'use'
            ? `課${Number(refOrg?.divisionId ?? 0)} / チーム${Number(refOrg?.teamId ?? 0)}を参照`
            : '参照なし'
        );

        next[t.id] = {
          value: String(prev[t.id]?.value ?? row?.value ?? t.name),
          bodyMax: String(prev[t.id]?.bodyMax ?? ((typeof row?.body_max_length === 'number') ? row.body_max_length : '')),
          refHint: String(prev[t.id]?.refHint ?? refHint),
          startFiscalYear: (toYearNumber(prev[t.id]?.startFiscalYear) != null
            ? prev[t.id].startFiscalYear
            : toYearNumber((row as any)?.start_fiscal_year ?? (row as any)?.team_start_fiscal_year)),
        };
      });
      return next;
    });

    const currentIds = new Set(teamRows.map((t: TeamItem) => t.id));
    setBuckets((prev) => {
      const nextPastByFy = buildEmptyPastByFy();
      const nextLatest: string[] = [];
      const nextDisabled: string[] = [];
      const placed = new Set<string>();

      const placeUnique = (container: 'latest' | 'disabled' | number, id: string) => {
        if (!currentIds.has(id) || placed.has(id)) return;
        if (container === 'latest') {
          nextLatest.push(id);
        } else if (container === 'disabled') {
          nextDisabled.push(id);
        } else {
          nextPastByFy[container] = [...(nextPastByFy[container] ?? []), id];
        }
        placed.add(id);
      };

      // 既存配置（未保存のドラッグ結果）を優先して維持
      prev.latest.forEach((id) => placeUnique('latest', id));
      prev.disabled.forEach((id) => placeUnique('disabled', id));
      fiscalYears.forEach((year) => {
        (prev.pastByFy?.[year] ?? []).forEach((id) => placeUnique(year, id));
      });

      // 新規追加など、まだ未配置のIDだけを初期ルールで配置
      teamRows.forEach((t: TeamItem) => {
        if (placed.has(t.id)) return;

        const row = bySourceId.get(t.id) ?? (typeof t.sourceIndex === 'number' ? (sourceRows[t.sourceIndex] ?? {}) : {});
        const status = String(row?.status ?? 'Y').toUpperCase();
        const endFiscalYear = toYearNumber((row as any)?.end_fiscal_year ?? (row as any)?.team_fiscal_year);

        if (status === 'N') {
          placeUnique('disabled', t.id);
          return;
        }
        if (typeof endFiscalYear === 'number' && fiscalYears.includes(endFiscalYear)) {
          placeUnique(endFiscalYear, t.id);
          return;
        }
        placeUnique('latest', t.id);
      });

      return { latest: nextLatest, disabled: nextDisabled, pastByFy: nextPastByFy };
    });
  }, [open, teamRows, sourceRows, fiscalYears, buildEmptyPastByFy, toYearNumber]);

  const byId = React.useMemo(() => {
    const m = new Map<string, TeamItem>();
    teamRows.forEach((t: TeamItem) => m.set(t.id, t));
    return m;
  }, [teamRows]);

  const findContainer = React.useCallback((id: string): string | undefined => {
    const b = bucketsRef.current;
    if (id === CONTAINER_LATEST || b.latest.includes(id)) return CONTAINER_LATEST;
    if (id === CONTAINER_DISABLED || b.disabled.includes(id)) return CONTAINER_DISABLED;
    for (const year of fiscalYears) {
      const pid = pastContainerId(year);
      if (id === pid || (b.pastByFy?.[year] ?? []).includes(id)) return pid;
    }
    return undefined;
  }, [fiscalYears]);

  const updateUi = (id: string, patch: Partial<TeamUiState>) => {
    setUiById((prev) => ({ ...prev, [id]: { ...(prev[id] ?? { value: '', bodyMax: '', refHint: '参照なし', startFiscalYear: null }), ...patch } }));
  };

  const handleDragStart = (event: any) => {
    setActiveId(String(event.active.id));
  };

  const handleDragOver = (event: any) => {
    const overId = event.over?.id;
    if (!overId) {
      setOverContainerId(null);
      return;
    }
    const overStr = String(overId);
    const overContainer = findContainer(overStr);
    setOverContainerId(overContainer ?? null);
  };

  const handleDragEnd = (event: any) => {
    setActiveId(null);
    setOverContainerId(null);

    const { active, over } = event;
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);
    const activeContainer = String(active.data.current?.containerId ?? findContainer(activeId) ?? '');
    const overContainer = findContainer(overId);
    if (!activeContainer || !overContainer) return;

    setBuckets((prev) => {
      const next: TeamBuckets = {
        latest: [...prev.latest],
        disabled: [...prev.disabled],
        pastByFy: { ...prev.pastByFy },
      };
      fiscalYears.forEach((year) => {
        next.pastByFy[year] = [...(prev.pastByFy?.[year] ?? [])];
      });

      const removeFromAll = (id: string) => {
        next.latest = next.latest.filter((v) => v !== id);
        next.disabled = next.disabled.filter((v) => v !== id);
        fiscalYears.forEach((year) => {
          next.pastByFy[year] = next.pastByFy[year].filter((v) => v !== id);
        });
      };

      if (activeContainer === overContainer) {
        if (overId === activeContainer) return prev;

        if (activeContainer === CONTAINER_LATEST) {
          const oldIndex = next.latest.indexOf(activeId);
          const newIndex = next.latest.indexOf(overId);
          if (oldIndex < 0 || newIndex < 0) return prev;
          next.latest = arrayMove(next.latest, oldIndex, newIndex);
          return next;
        }

        if (activeContainer === CONTAINER_DISABLED) {
          const oldIndex = next.disabled.indexOf(activeId);
          const newIndex = next.disabled.indexOf(overId);
          if (oldIndex < 0 || newIndex < 0) return prev;
          next.disabled = arrayMove(next.disabled, oldIndex, newIndex);
          return next;
        }

        const year = parsePastContainerYear(activeContainer);
        if (!year) return prev;
        const list = next.pastByFy[year] ?? [];
        const oldIndex = list.indexOf(activeId);
        const newIndex = list.indexOf(overId);
        if (oldIndex < 0 || newIndex < 0) return prev;
        next.pastByFy[year] = arrayMove(list, oldIndex, newIndex);
        return next;
      }

      removeFromAll(activeId);

      if (overContainer === CONTAINER_LATEST) {
        next.latest = [...next.latest, activeId];
        return next;
      }
      if (overContainer === CONTAINER_DISABLED) {
        next.disabled = [...next.disabled, activeId];
        return next;
      }

      const targetYear = parsePastContainerYear(overContainer);
      if (!targetYear) return prev;
      next.pastByFy[targetYear] = [...(next.pastByFy[targetYear] ?? []), activeId];
      return next;
    });
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setOverContainerId(null);
  };

  const clickAddButtonInBaseDialog = React.useCallback(() => {
    const hiddenRoot = hiddenCardRef.current;
    if (hiddenRoot) {
      const hiddenButtons = Array.from(hiddenRoot.querySelectorAll('button')) as HTMLButtonElement[];
      const hiddenAdd = hiddenButtons.find((b) => String(b.textContent ?? '').trim() === '追加');
      if (hiddenAdd && !hiddenAdd.disabled) {
        hiddenAdd.click();
        return;
      }
    }

    const root = document.querySelector('.setting-dialog-root');
    if (!root) return;
    const buttons = Array.from(root.querySelectorAll('button')) as HTMLButtonElement[];
    const add = buttons.find((b) => {
      if (String(b.textContent ?? '').trim() !== '追加') return false;
      const inMock = b.closest('[data-team-setting-mock="1"]');
      return !inMock;
    });
    if (add && !add.disabled) add.click();
  }, []);

  const toFiniteNumber = React.useCallback((v: any): number | null => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }, []);

  const teamItemMatchContext = React.useMemo(() => {
    const byTeamId = new Map<number, TeamItem>();
    const byOptionId = new Map<number, TeamItem>();
    const bySourceIndex = new Map<number, TeamItem>();
    const byNameOccurrence = new Map<string, TeamItem>();
    const nameCounter = new Map<string, number>();

    teamRows.forEach((t: TeamItem) => {
      const src = typeof t.sourceIndex === 'number' ? (sourceRows[t.sourceIndex] ?? {}) : {};
      const teamId = toFiniteNumber((src as any)?.team_id);
      const optionId = toFiniteNumber((src as any)?.option_id);

      if (teamId != null) byTeamId.set(teamId, t);
      if (optionId != null) byOptionId.set(optionId, t);
      if (typeof t.sourceIndex === 'number') bySourceIndex.set(t.sourceIndex, t);

      const keyName = String(t.name ?? '').trim();
      const n = (nameCounter.get(keyName) ?? 0) + 1;
      nameCounter.set(keyName, n);
      byNameOccurrence.set(`${keyName}__${n}`, t);
    });

    return { byTeamId, byOptionId, bySourceIndex, byNameOccurrence };
  }, [teamRows, sourceRows, toFiniteNumber]);

  const teamItemIdsBySourceTeamId = React.useMemo(() => {
    const map = new Map<number, string[]>();
    teamRows.forEach((item: TeamItem) => {
      if (typeof item.sourceIndex !== 'number') return;
      const src = sourceRows[item.sourceIndex] ?? {};
      const teamIdNum = Number((src as any)?.team_id);
      if (!Number.isFinite(teamIdNum)) return;
      map.set(teamIdNum, [...(map.get(teamIdNum) ?? []), item.id]);
    });
    return map;
  }, [teamRows, sourceRows]);

  const teamItemIdsBySourceOptionId = React.useMemo(() => {
    const map = new Map<number, string[]>();
    teamRows.forEach((item: TeamItem) => {
      if (typeof item.sourceIndex !== 'number') return;
      const src = sourceRows[item.sourceIndex] ?? {};
      const optionIdNum = Number((src as any)?.option_id);
      if (!Number.isFinite(optionIdNum)) return;
      map.set(optionIdNum, [...(map.get(optionIdNum) ?? []), item.id]);
    });
    return map;
  }, [teamRows, sourceRows]);

  const teamItemIdBySourceIndex = React.useMemo(() => {
    const map = new Map<number, string>();
    teamRows.forEach((item: TeamItem) => {
      if (typeof item.sourceIndex !== 'number') return;
      map.set(item.sourceIndex, item.id);
    });
    return map;
  }, [teamRows]);

  const orderedTeamIdsForSubmit = React.useMemo(() => {
    const ordered: string[] = [];
    const seen = new Set<string>();
    const pushUnique = (id: string) => {
      if (!id || seen.has(id)) return;
      seen.add(id);
      ordered.push(id);
    };

    buckets.latest.forEach((id) => pushUnique(id));
    fiscalYears.forEach((year) => {
      (buckets.pastByFy?.[year] ?? []).forEach((id) => pushUnique(id));
    });
    buckets.disabled.forEach((id) => pushUnique(id));
    teamRows.forEach((item: TeamItem) => pushUnique(item.id));

    return ordered;
  }, [buckets, fiscalYears, teamRows]);

  const modeByTeamId = React.useMemo(() => {
    const map = new Map<string, { mode: 'latest' | 'past' | 'disabled'; fiscalYear: number | null }>();
    buckets.latest.forEach((id) => map.set(id, { mode: 'latest', fiscalYear: null }));
    buckets.disabled.forEach((id) => map.set(id, { mode: 'disabled', fiscalYear: null }));
    fiscalYears.forEach((year) => {
      (buckets.pastByFy?.[year] ?? []).forEach((id) => {
        map.set(id, { mode: 'past', fiscalYear: year });
      });
    });
    return map;
  }, [buckets, fiscalYears]);

  const orderNumberByTeamId = React.useMemo(() => {
    const orderedIds: string[] = [];
    orderedIds.push(...buckets.latest);
    fiscalYears.forEach((year) => {
      orderedIds.push(...(buckets.pastByFy?.[year] ?? []));
    });
    orderedIds.push(...buckets.disabled);

    const map = new Map<string, number>();
    orderedIds.forEach((id, index) => {
      map.set(id, index + 1);
    });
    return map;
  }, [buckets, fiscalYears]);

  const handleAcceptFromDialog = React.useCallback((incomingMeta: any) => {
    const hasEmptyTeamName = teamRows.some((t: TeamItem) => {
      const currentValue = uiById[t.id]?.value ?? t.name;
      return String(currentValue ?? '').trim() === '';
    });
    if (hasEmptyTeamName) {
      setNotifyMsg('チーム名を入れてください');
      setNotifyOpen(true);
      return false;
    }

    const next = { ...(incomingMeta ?? {}) } as any;
    if (!Array.isArray(next.options)) {
      onAccept?.(next);
      return;
    }

    const options = (next.options as any[]).map((opt: any) => ({ ...opt }));
    const usedTeamItemIds = new Set<string>();
    const namedMatchCounter = new Map<string, number>();
    const mappedByTeamId = new Map<string, { teamItem: TeamItem; option: any }>();
    const unmatchedOptions: any[] = [];

    const pickUnused = (ids?: string[]) => {
      if (!Array.isArray(ids) || ids.length === 0) return null;
      const id = ids.find((candidate) => !usedTeamItemIds.has(candidate));
      return id ?? null;
    };

    options.forEach((opt: any, optionIndex: number) => {
      const keyName = String(opt?.value ?? opt?.label ?? '').trim();
      const nextCount = (namedMatchCounter.get(keyName) ?? 0) + 1;
      namedMatchCounter.set(keyName, nextCount);

      const teamIdNum = Number((opt as any)?.team_id);
      const optionIdNum = Number((opt as any)?.option_id);

      // 新規追加行は SettingDialog が付与した安定識別子(__ui_uid)で確実に突合する。
      // team_id/option_id が無い新規行は、これが無いと「名前＋出現回数」の脆いフォールバックに頼るしかなく、
      // 複数チームを同時追加した際に取り違えが起きていた。
      const uiUid = String((opt as any)?.__ui_uid ?? '').trim();
      const uiUidTeamItemId = uiUid ? `uid-${uiUid}` : '';

      const teamItemId =
        (uiUidTeamItemId && byId.has(uiUidTeamItemId) && !usedTeamItemIds.has(uiUidTeamItemId)
          ? uiUidTeamItemId
          : null) ??
        pickUnused(Number.isFinite(teamIdNum) ? teamItemIdsBySourceTeamId.get(teamIdNum) : undefined) ??
        pickUnused(Number.isFinite(optionIdNum) ? teamItemIdsBySourceOptionId.get(optionIdNum) : undefined) ??
        (teamItemIdBySourceIndex.has(optionIndex) && !usedTeamItemIds.has(teamItemIdBySourceIndex.get(optionIndex) as string)
          ? (teamItemIdBySourceIndex.get(optionIndex) as string)
          : null) ??
        (() => {
          const fallback = teamItemMatchContext.byNameOccurrence.get(`${keyName}__${nextCount}`);
          if (!fallback || usedTeamItemIds.has(fallback.id)) return null;
          return fallback.id;
        })();

      if (!teamItemId) {
        unmatchedOptions.push(opt);
        return;
      }

      const teamItem = byId.get(teamItemId);
      if (!teamItem) {
        unmatchedOptions.push(opt);
        return;
      }

      usedTeamItemIds.add(teamItemId);
      mappedByTeamId.set(teamItemId, { teamItem, option: opt });
    });

    const normalizedByOrder: any[] = [];
    const consumedIds = new Set<string>();

    orderedTeamIdsForSubmit.forEach((teamItemId) => {
      const mapped = mappedByTeamId.get(teamItemId);
      if (!mapped) return;

      consumedIds.add(teamItemId);
      const out = { ...mapped.option };
      const ui = uiById[mapped.teamItem.id];

      if (ui) {
        out.value = String(ui.value ?? '').trim();
        const raw = String(ui.bodyMax ?? '').trim();
        if (raw === '') {
          out.team_body_max_length = null;
        } else {
          const num = Math.floor(Number(raw));
          out.team_body_max_length = Number.isFinite(num) && num > 0 ? num : null;
        }
        out.start_fiscal_year = typeof ui.startFiscalYear === 'number' ? ui.startFiscalYear : null;
      }

      const modeState = modeByTeamId.get(mapped.teamItem.id) ?? { mode: 'latest' as const, fiscalYear: null };
      out.status = modeState.mode === 'disabled' ? 'N' : 'Y';
      out.team_setting_mode = modeState.mode;
      out.end_fiscal_year = modeState.mode === 'past' ? modeState.fiscalYear : null;
      const orderNumber = orderNumberByTeamId.get(mapped.teamItem.id);
      if (typeof orderNumber === 'number') {
        out.order_number = orderNumber;
      }
      delete out.team_start_fiscal_year;
      delete out.team_fiscal_year;
      normalizedByOrder.push(out);
    });

    mappedByTeamId.forEach((mapped, teamItemId) => {
      if (consumedIds.has(teamItemId)) return;
      const out = { ...mapped.option };
      const ui = uiById[mapped.teamItem.id];
      if (ui) {
        out.value = String(ui.value ?? '').trim();
        const raw = String(ui.bodyMax ?? '').trim();
        if (raw === '') {
          out.team_body_max_length = null;
        } else {
          const num = Math.floor(Number(raw));
          out.team_body_max_length = Number.isFinite(num) && num > 0 ? num : null;
        }
        out.start_fiscal_year = typeof ui.startFiscalYear === 'number' ? ui.startFiscalYear : null;
      }

      const modeState = modeByTeamId.get(mapped.teamItem.id) ?? { mode: 'latest' as const, fiscalYear: null };
      out.status = modeState.mode === 'disabled' ? 'N' : 'Y';
      out.team_setting_mode = modeState.mode;
      out.end_fiscal_year = modeState.mode === 'past' ? modeState.fiscalYear : null;
      delete out.team_start_fiscal_year;
      delete out.team_fiscal_year;
      normalizedByOrder.push(out);
    });

    if (unmatchedOptions.length > 0) {
      console.warn('[TeamSettingDialog] Some options could not be mapped to drag order; appended to tail.', {
        unmatchedCount: unmatchedOptions.length,
      });
      normalizedByOrder.push(...unmatchedOptions);
    }

    next.options = normalizedByOrder;

    onAccept?.(next);
  }, [
    onAccept,
    teamRows,
    teamItemMatchContext,
    uiById,
    modeByTeamId,
    teamItemIdsBySourceTeamId,
    teamItemIdsBySourceOptionId,
    teamItemIdBySourceIndex,
    byId,
    orderedTeamIdsForSubmit,
    orderNumberByTeamId,
    toFiniteNumber,
  ]);

  const renderTeamCard = (teamId: string, containerId: string, variant: 'current' | 'past' | 'archived' = 'current') => {
    const item = byId.get(teamId);
    if (!item) return null;
    const ui = uiById[item.id] ?? { value: item.name, bodyMax: '', refHint: item.refHint ?? '参照なし', startFiscalYear: null };
    const refTooltip = item.refMeta ? (
      <Box sx={{ p: 0.5 }}>
        <Typography variant="caption" sx={{ display: 'block' }}>以下の課・チームのマスタ設定をコピーします。</Typography>
        <Typography variant="caption" sx={{ display: 'block' }}>
          課: {divisionLabelById.get(Number(item.refMeta.divisionId)) || String(item.refMeta.divisionId ?? '-')}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block' }}>
          チーム: {teamLabelById.get(Number(item.refMeta.teamId)) || String(item.refMeta.teamId ?? '-')}
        </Typography>
        {item.copySameEnlightConnection ? (
          <Typography variant="caption" sx={{ display: 'block' }}>
            ※同じEnlightID連携を設定します。
          </Typography>
        ) : null}
      </Box>
    ) : (
      <Box sx={{ p: 0.5 }}>
        <Typography variant="caption" sx={{ display: 'block' }}>以下の課・チームのマスタ設定をコピーします。</Typography>
        <Typography variant="caption" sx={{ display: 'block' }}>{item.refHint ?? '参照元が設定されています'}</Typography>
        {item.copySameEnlightConnection ? (
          <Typography variant="caption" sx={{ display: 'block' }}>
            ※同じEnlightID連携を設定します。
          </Typography>
        ) : null}
      </Box>
    );

    return (
      <TeamCard
        key={item.id}
        item={item}
        containerId={containerId}
        isNew={Boolean(item.isNew)}
        refTooltip={refTooltip}
        ui={ui}
        variant={variant}
        fiscalYears={fiscalYears}
        onStartFiscalYearChange={(id, year) => updateUi(id, { startFiscalYear: year })}
        onValueChange={(id, value) => updateUi(id, { value })}
        onBodyMaxChange={(id, bodyMax) => updateUi(id, { bodyMax })}
      />
    );
  };

  const renderCurrentContainer = () => (
    <Grid size={{ xs: 12, md: 7 }}>
      <Paper variant="outlined" sx={{ p: 1.5, minHeight: 320, background: isDark ? '#0f172a' : '#fafafa' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>現在チーム</Typography>
          <Chip size="small" label={`${buckets.latest.length}件`} />
        </Box>
        <SortableContext items={buckets.latest} strategy={verticalListSortingStrategy}>
          <DroppableArea id={CONTAINER_LATEST} active={overContainerId === CONTAINER_LATEST}>
            <Box
              sx={{
                border: isDark ? '1px dashed #334155' : '1px dashed #d2d2d2',
                borderRadius: 1,
                backgroundColor: isDark ? '#1e293b' : '#fff',
                minHeight: 245,
                p: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              {buckets.latest.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  ここにドラッグ
                </Typography>
              )}
              {buckets.latest.map((teamId) => {
                const item = byId.get(teamId);
                if (!item) return null;
                const ui = uiById[item.id] ?? { value: item.name, bodyMax: '', refHint: item.refHint ?? '参照なし' };
                const refTooltip = item.refMeta ? (
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="caption" sx={{ display: 'block' }}>以下の課・チームのマスタ設定をコピーします。</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      課: {divisionLabelById.get(Number(item.refMeta.divisionId)) || String(item.refMeta.divisionId ?? '-')}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      チーム: {teamLabelById.get(Number(item.refMeta.teamId)) || String(item.refMeta.teamId ?? '-')}
                    </Typography>
                    {item.copySameEnlightConnection ? (
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        ※同じEnlightID連携を設定します。
                      </Typography>
                    ) : null}
                  </Box>
                ) : (
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="caption" sx={{ display: 'block' }}>以下の課・チームのマスタ設定をコピーします。</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>{item.refHint ?? '参照元が設定されています'}</Typography>
                    {item.copySameEnlightConnection ? (
                      <Typography variant="caption" sx={{ display: 'block' }}>
                        ※同じEnlightID連携を設定します。
                      </Typography>
                    ) : null}
                  </Box>
                );
                return (
                  <TeamCard
                    key={item.id}
                    item={item}
                    containerId={CONTAINER_LATEST}
                    isNew={Boolean(item.isNew)}
                    refTooltip={refTooltip}
                    ui={ui}
                    variant="current"
                    onValueChange={(id, value) => updateUi(id, { value })}
                    onBodyMaxChange={(id, bodyMax) => updateUi(id, { bodyMax })}
                  />
                );
              })}
            </Box>
          </DroppableArea>
        </SortableContext>
      </Paper>
    </Grid>
  );

  const renderArchivedContainer = () => (
    <Grid size={{ xs: 12, md: 5 }}>
      <Paper variant="outlined" sx={{ p: 1.5, minHeight: 320, background: isDark ? '#0f172a' : '#e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>過去・旧名称チーム</Typography>
          <Chip
            size="small"
            label={`${fiscalYears.reduce((acc, y) => acc + ((buckets.pastByFy?.[y] ?? []).length), 0) + buckets.disabled.length}件`}
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Box sx={{ border: isDark ? '1px solid #334155' : '1px solid #c9c9c9', borderRadius: 1, backgroundColor: isDark ? '#1e293b' : '#e8e8e8', p: 1 }}>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.5 }}>
              過去年度
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 360, overflowY: 'auto', pr: 0.5 }}>
              {fiscalYears.map((year) => {
                const containerId = pastContainerId(year);
                const ids = buckets.pastByFy?.[year] ?? [];
                return (
                  <SortableContext key={containerId} items={ids} strategy={verticalListSortingStrategy}>
                    <DroppableArea id={containerId} active={overContainerId === containerId}>
                      <Box sx={{ border: isDark ? '1px dashed #334155' : '1px dashed #bdbdbd', borderRadius: 1, backgroundColor: isDark ? '#0f172a' : '#ececec', p: 0.75 }}>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.5 }}>
                          {toFyLabel(year)}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minHeight: 30 }}>
                          {ids.length === 0 && (
                            <Typography variant="caption" color="text.secondary">
                              ここにドラッグ
                            </Typography>
                          )}
                          {ids.map((teamId) => renderTeamCard(teamId, containerId, 'past'))}
                        </Box>
                      </Box>
                    </DroppableArea>
                  </SortableContext>
                );
              })}
            </Box>
          </Box>

          <Box sx={{ border: isDark ? '1px solid #334155' : '1px solid #c9c9c9', borderRadius: 1, backgroundColor: isDark ? '#1e293b' : '#e8e8e8', p: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>削除（無効）</Typography>
              <Chip size="small" label={`${buckets.disabled.length}件`} />
            </Box>
            <SortableContext items={buckets.disabled} strategy={verticalListSortingStrategy}>
              <DroppableArea id={CONTAINER_DISABLED} active={overContainerId === CONTAINER_DISABLED}>
                <Box sx={{ border: isDark ? '1px dashed #334155' : '1px dashed #bdbdbd', borderRadius: 1, backgroundColor: isDark ? '#0f172a' : '#ececec', p: 0.75, minHeight: 64, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {buckets.disabled.length === 0 && (
                    <Typography variant="caption" color="text.secondary">
                      ここにドラッグ
                    </Typography>
                  )}
                  {buckets.disabled.map((teamId) => renderTeamCard(teamId, CONTAINER_DISABLED, 'archived'))}
                </Box>
              </DroppableArea>
            </SortableContext>
          </Box>
        </Box>
      </Paper>
    </Grid>
  );

  const mockCard = portalTarget
    ? createPortal(
        <Card data-team-setting-mock="1" sx={{ backgroundColor: isDark ? '#1e293b' : '#eef6ff', border: isDark ? '1px solid #0284c7' : '1px solid #6aa6ff' }}>
          <CardContent sx={{ pt: 3 }}>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 1 }}>
              値設定、および有効/無効化（チーム年度対応UI）
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                チーム設定をカード単位で編集しながら、現在／過去（FY別）／無効をドラッグで分類します。
              </Typography>
              <Button size="small" variant="contained" color="success" onClick={clickAddButtonInBaseDialog}>
                新規チームの追加
              </Button>
            </Box>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <Grid container spacing={1.5}>
                {renderCurrentContainer()}
                {renderArchivedContainer()}
              </Grid>

              <DragOverlay>
                {activeId ? (
                  <Box
                    sx={{
                      border: isDark ? '1px solid #334155' : '1px solid #cfcfcf',
                      borderRadius: 1,
                      p: 1,
                      backgroundColor: isDark ? '#1e293b' : '#fff',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.18)',
                      minWidth: 180,
                    }}
                  >
                    <Typography variant="body2">{byId.get(activeId)?.name ?? ''}</Typography>
                  </Box>
                ) : null}
              </DragOverlay>
            </DndContext>
          </CardContent>
        </Card>,
        portalTarget
      )
    : null;

  return (
    <>
      <SettingDialog {...props} onAccept={handleAcceptFromDialog} />
      {open && mockCard}
      <Snackbar open={notifyOpen} message={notifyMsg} onClose={() => setNotifyOpen(false)} />
    </>
  );
}

export default TeamSettingDialog;
